import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AES, enc } from "crypto-js";
import { useDispatch, useSelector } from "react-redux";

import Timer from "./Timer";
import {
  clearExpireTime,
  clearUserInfo,
  hideIdleModal,
  hideLoginModal,
  hideModal,
} from "../../store/auth/authSlice";

import { SessionExpired } from "./SessionExpired";
import { Modal } from "./Modal";
import { useSessionManager } from "../../Hooks/useSessionManager";
import { useLogOutUserMutation } from "../../store/auth/usersApiSlice";

import { ReactComponent as TrevexLogo } from "../../assets/trevexLogo.svg";
import Login from "../../pages/public/Login";

const {
  REACT_APP_VERIFICATION_CODE,
  REACT_APP_PUBLIC_ACCESS_TOKEN,
  REACT_APP_ENVIRONMENT,
} = process.env;

// Routes nest PrivateRoute inside PrivateRoute (a layout wraps one and the
// child route wraps another — /search/:id sits under RootLayout, for example),
// so without this flag the session/login modals render twice, stacked on
// identical fixed overlays. Each copy keeps its own form state, which made a
// re-login in the popup appear to do nothing until it was entered twice.
// Only the outermost PrivateRoute owns the timers and the modals.
const SessionModalOwnerContext = createContext(false);

const PrivateRoute = ({ children, token = true }) => {
  const isOwner = !useContext(SessionModalOwnerContext);

  useSessionManager(isOwner);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logOutUser] = useLogOutUserMutation();

  const isPrompt = location.pathname.includes("prompt");

  const { showIdleModal, showLoginModal, isModalVisible, token: userToken, modalType } = useSelector(
    (state) => state.auth
  );

  // const isIdle = showIdleModal; // This modal only handles idle
  const isExpiry = modalType === "expiry";

  const otp = localStorage.getItem("appvalue");

  let originalText = "";

  try {
    const bytes = AES.decrypt(
      otp ?? "",
      REACT_APP_PUBLIC_ACCESS_TOKEN
    );

    originalText = bytes.toString(enc.Utf8);
  } catch (err) {
    console.error("OTP decrypt error:", err);
    console.error("Decrypt error:", err);
  }

  const isVerified =
    ((REACT_APP_ENVIRONMENT === "testing" ||
      REACT_APP_ENVIRONMENT === "development") &&
      originalText === REACT_APP_VERIFICATION_CODE) ||
    REACT_APP_ENVIRONMENT === "production";

  const isAuthenticated = !!userToken;

  //  Redirects (Side Effects)

  // Verification redirect
  // Redirect to verification if not verified
  useEffect(() => {
    if (!isVerified) {
      navigate("/verification", { replace: true });
    }
  }, [isVerified, navigate]);

  // Login redirect
  // Redirect to login if token required
  useEffect(() => {
    if (token && !isAuthenticated) {
      // TIP-599 — a guest hitting a gated route directly (e.g. /claim/:id typed
      // or shared as a URL) is bounced to login, and the blocked path is kept as
      // `lastVisited` so Login returns the user to it after authenticating
      // instead of dropping them on the dashboard.
      const intendedPath = `${location.pathname}${location.search || ""}`;

      if (!intendedPath.startsWith("/login")) {
        localStorage.setItem("lastVisited", intendedPath);
      }

      navigate(isPrompt ? "/login/prompt" : "/login", {
        replace: true,
      });
    }
  }, [token, isAuthenticated, isPrompt, navigate, location.pathname, location.search]);

  /* --------------------------------
     Modal Handlers
  -------------------------------- */

  const handleCloseModal = async () => {
    // End the SERVER session too. Clearing only our side leaves the backend's
    // OutstandingToken alive, and sign-in is single-session — so the next
    // login answers ALREADY_LOGGED_IN and the customer is bounced into the
    // session-conflict dialog. Best effort: a token already rejected cannot
    // log itself out, and the local clear has to happen either way.
    try {
      await logOutUser({ token: userToken }).unwrap();
    } catch (err) {
      // Already expired, already blacklisted, or offline.
    }

    dispatch(clearUserInfo());
    dispatch(hideModal());

    navigate(isPrompt ? "/login/prompt" : "/login", {
      replace: true,
    });
  };

  const handleClose = () => {
    dispatch(hideIdleModal());
    if (isExpiry) {
      dispatch(clearExpireTime());
    }
  };
  /* --------------------------------
     Block Render if Redirecting
  -------------------------------- */

  if (!isVerified) return null;

  if (token && !isAuthenticated) return null;

  /* --------------------------------
     Render
  -------------------------------- */

  return (
    <SessionModalOwnerContext.Provider value={true}>
      {/* Session Timer */}
      {isOwner && <Timer />}

      {/* Protected Content */}
      {children}

      {isOwner && (
        <>
          {/* Duplicate Login Modal */}
          {isModalVisible && (
            <Modal modalHandle={handleCloseModal}>
              <SessionExpired
                message="Duplicate Login Detected"
                subMessage="We noticed your account is active elsewhere. Please sign in again."
              />
            </Modal>
          )}

          {/* Login Required Modal */}
          {showLoginModal && (
            <Modal modalHandle={() => dispatch(hideLoginModal())}>
              <div className="w-[449px] px-[49px] py-8 flex flex-col justify-between">
                <div className="w-full flex justify-center">
                  <TrevexLogo />
                </div>

                <h2 className="text-xl font-semibold text-center">
                  Login Required
                </h2>

                <Login modalLogin preventRoute />
              </div>
            </Modal>
          )}

          {/* Idle / Expiry Modal */}
          {showIdleModal && (
            <Modal modalHandle={() => handleClose()}>
              <SessionExpired />
            </Modal>
          )}
        </>
      )}
    </SessionModalOwnerContext.Provider>
  );
};

export default PrivateRoute;
